import { BraintreeError } from 'braintree-web';
import { computed, ref } from 'vue';

import {
  confirmUSBankAccount as confirmUSBankAccountApi,
  defaultPaymentMethod,
  deletePaymentMethod,
  verifyUSBankAccount as verifyUSBankAccountApi,
} from '@/api/paymentMethod';
import {
  BaseUSBankAccountDetails,
  TokenizedUSBankAccountResponse,
  USBankAccountDetailsBusiness,
  USBankAccountDetailsPersonal,
  useBraintree,
} from '@/stores/braintree';
import { useCustomer } from '@/stores/customer';
import { NutsAddress } from '@/utils/address';
import { NutsRedirect } from '@/utils/exceptions';
import { reportError } from '@/utils/reportError';

export interface USBankAccountDetails {
  accountNumber: string;
  businessName?: string;
  confirmAccountNumber: string;
  firstName?: string;
  isBusiness?: boolean;
  lastName?: string;
  routingNumber: string;
}
// eslint-disable-next-line no-shadow
export enum USBankAccountStatus {
  AWAITING_MICRO_TRANSFER_AMOUNTS = 'Awaiting Micro-Transfer Amounts',
  AWAITING_MICRO_TRANSFER_SETTLEMENTS = 'Awaiting Micro-Transfer Settlements',
  DECLINED = 'Declined',
  MICRO_TRANSFERS_REQUEST_FAILED = 'Micro-Transfers Request Failed',
  NETWORK_CHECK_REQUEST_FAILED = 'Network Check Request Failed',
  VERIFIED = 'Verified',
}

export function useWallet() {
  const { tokenizeUSBankAccountDetails } = useBraintree();

  const customerId = computed(() => useCustomer().customer?.id ?? '');
  const braintreeId = computed(
    () => useCustomer().customer?.custom?.fields?.braintreeId ?? undefined,
  );

  const nonce = ref('');
  const synthesizedPaymentMethod = ref<TokenizedUSBankAccountResponse | undefined>();

  const formatAccountInfo = (accountDetails: USBankAccountDetails, billingAddress: NutsAddress) => {
    const { accountNumber, routingNumber, isBusiness } = accountDetails;
    const baseBankDetails: BaseUSBankAccountDetails = {
      accountNumber,
      routingNumber,
      accountType: 'checking',
      billingAddress: NutsAddress.toBraintree(billingAddress),
    };

    let bankDetails: USBankAccountDetailsBusiness | USBankAccountDetailsPersonal;

    if (isBusiness) {
      bankDetails = {
        ...baseBankDetails,
        businessName: accountDetails.businessName ?? '',
        ownershipType: 'business',
      };
    } else {
      bankDetails = {
        ...baseBankDetails,
        firstName: accountDetails.firstName ?? '',
        lastName: accountDetails.lastName ?? '',
        ownershipType: 'personal',
      };
    }
    return bankDetails;
  };

  const verifyUSBankAccount = async ({
    deviceData,
    paymentId,
  }: {
    deviceData?: string;
    paymentId?: number;
  }): Promise<{
    status?: string;
    paymentMethodId?: number;
    error?: string;
    lastFourDigits?: string;
  }> => {
    const api = await verifyUSBankAccountApi({
      customerId: customerId.value,
      braintreeId: braintreeId.value,
      deviceData,
      synthesizedPaymentMethod: synthesizedPaymentMethod.value!,
      paymentMethodId: paymentId,
    })
      .then((response) => response.data)
      .catch((error) => ({ error }));

    return api;
  };

  const submitUSBankAccount = async ({
    accountDetails,
    billingAddress,
    mandateText,
    paymentId,
    setAsDefault,
    deviceData,
  }: {
    accountDetails: USBankAccountDetails;
    billingAddress: NutsAddress;
    mandateText: string;
    paymentId?: number;
    setAsDefault?: boolean;
    deviceData?: string;
  }) => {
    const bankDetails = formatAccountInfo(accountDetails, billingAddress);
    let data;
    let error;

    await tokenizeUSBankAccountDetails(bankDetails, mandateText)
      .then((response) => {
        synthesizedPaymentMethod.value = response;
        synthesizedPaymentMethod.value = { ...synthesizedPaymentMethod.value, setAsDefault };
        nonce.value = synthesizedPaymentMethod.value.nonce;
      })
      .catch((err: BraintreeError) => {
        if (err.details?.originalError[0]?.message) {
          error = err.details.originalError[0].message;
        } else {
          error = err.message;
        }
      });

    if (error) return { error };

    if (nonce.value) {
      data = await verifyUSBankAccount({ paymentId, deviceData });

      return {
        status: data.status,
        id: data.paymentMethodId,
        error: data.error,
        lastFour: data.lastFourDigits,
      };
    }

    return null;
  };

  const confirmUSBankAccount = async ({
    paymentId,
    amount1,
    amount2,
    deviceData,
  }: {
    paymentId: number;
    amount1: number;
    amount2: number;
    deviceData?: string;
  }): Promise<{ error?: string; paymentMethodId?: number; status?: string }> => {
    try {
      const response = await confirmUSBankAccountApi({
        customerId: customerId.value,
        paymentMethodId: paymentId,
        amounts: [amount1, amount2],
        deviceData,
      });

      return {
        error: response.data.error,
        paymentMethodId: response.data.paymentMethodId,
        status: response.data.status,
      };
    } catch (error) {
      if (error instanceof NutsRedirect) {
        return { error: error.errors[0].message };
      }
      const message = reportError(error);
      return { error: message };
    }
  };

  const deleteUSBankAccount = async (
    paymentId: number,
  ): Promise<{ notice?: string; error?: string }> => {
    try {
      const response = await deletePaymentMethod({
        customerId: customerId.value,
        paymentMethodId: paymentId,
      });
      return { notice: response.notices ? response.notices[0].message : '' };
    } catch (error) {
      if (error instanceof NutsRedirect) {
        return { error: error.errors[0].message };
      }
      const message = reportError(error);
      return { error: message };
    }
  };

  const setDefault = async (paymentId: number): Promise<{ notice?: string; error?: string }> => {
    try {
      await defaultPaymentMethod({
        customerId: customerId.value,
        paymentMethodId: paymentId,
      });
      return { notice: 'Default payment successfully set.' };
    } catch (error) {
      const message = reportError(error);
      return { error: message };
    }
  };

  return {
    confirmUSBankAccount,
    deleteUSBankAccount,
    setDefault,
    submitUSBankAccount,
    synthesizedPaymentMethod,
    verifyUSBankAccount,
  };
}
